<script setup lang="ts">
const { t } = useI18n({ useScope: "local" });

interface Category {
  value: string;
  label: string;
  icon: string;
}

interface CityOption {
  slug: string;
  name: string;
  count: number;
}

const props = defineProps<{
  modelValue: string;
  placeholder: string;
  selectedCategory: string;
  categories: ReadonlyArray<Category>;
  /** Liste des villes ayant ≥1 entité (filtre dynamique). Optionnel. */
  cities?: CityOption[];
  /** Slug de la ville actuellement sélectionnée (chip violet) */
  selectedVille?: string | null;
  /** Masque la searchbar pour n'afficher QUE le bouton Filtres
   *  (mode utilisé sur /recherche où la searchbar vit dans le header). */
  hideSearch?: boolean;
}>();

const emit = defineEmits<{
  "update:modelValue": [value: string];
  "update:selectedCategory": [value: string];
  "select-ville": [slug: string];
  "clear-ville": [];
}>();

const searchQuery = computed({
  get: () => props.modelValue,
  set: (v: string) => emit("update:modelValue", v),
});

const showFilters = ref(false);
// Filtre interne du picker villes (search-in-search)
const villeSearchQuery = ref("");

const filteredCities = computed(() => {
  const list = props.cities || [];
  if (!villeSearchQuery.value.trim()) return list.slice(0, 30); // limite affichage
  const q = villeSearchQuery.value.toLowerCase().trim();
  return list.filter((c) => c.name.toLowerCase().includes(q)).slice(0, 30);
});

const setCategory = (cat: string) => {
  emit("update:selectedCategory", cat);
  // Pas de fermeture auto : laisse l'user changer la ville aussi sans rouvrir
};

const selectVille = (slug: string) => {
  emit("select-ville", slug);
  showFilters.value = false;
};

const clearVille = () => {
  emit("clear-ville");
};

const hasActiveFilter = computed(
  () => props.selectedCategory !== "all" || !!props.selectedVille,
);
const activeFilterCount = computed(() => {
  let n = 0;
  if (props.selectedCategory !== "all") n++;
  if (props.selectedVille) n++;
  return n;
});
</script>

<template>
  <div>
    <div class="flex gap-3 items-center" :class="hideSearch ? '' : 'mb-4'">
      <!-- Searchbar (masquée quand le parent l'affiche ailleurs, ex header) -->
      <div v-if="!hideSearch" class="relative flex-1">
        <UIcon name="i-lucide-search" class="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 size-5" />
        <input
          v-model="searchQuery"
          type="text"
          :placeholder="placeholder"
          class="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-200 bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:border-gray-300 text-sm"
        />
        <button
          v-if="searchQuery"
          class="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          @click="searchQuery = ''"
        >
          <UIcon name="i-lucide-x" class="size-4" />
        </button>
      </div>
      <div v-if="categories.length > 0 || (cities && cities.length > 0)" class="relative shrink-0">
        <button
          class="flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium transition-colors border-2 border-transparent"
          :class="showFilters || hasActiveFilter
            ? 'bg-main text-white shadow-sm'
            : 'bg-main text-white hover:bg-main/90'"
          @click="showFilters = !showFilters"
        >
          <UIcon name="i-lucide-sliders-horizontal" class="size-4" />
          <span class="hidden sm:inline">{{ t('filtres.bouton') }}</span>
          <span v-if="activeFilterCount > 0" class="size-5 flex items-center justify-center rounded-full bg-white/20 text-[10px] font-bold">{{ activeFilterCount }}</span>
        </button>
        <!-- Popover -->
        <Transition
          enter-active-class="transition duration-200 ease-out"
          enter-from-class="opacity-0 translate-y-1"
          enter-to-class="opacity-100 translate-y-0"
          leave-active-class="transition duration-150 ease-in"
          leave-from-class="opacity-100 translate-y-0"
          leave-to-class="opacity-0 translate-y-1"
        >
          <div v-if="showFilters" class="absolute right-0 top-full mt-2 z-50 bg-white rounded-2xl shadow-xl border border-gray-200 p-4 w-80 max-h-[70vh] overflow-y-auto">
            <!-- ─── Filtre Ville ───────────────────────────────────────── -->
            <div v-if="cities && cities.length > 0" class="mb-5">
              <div class="flex items-center justify-between mb-2">
                <p class="text-sm font-semibold text-gray-900 flex items-center gap-1.5">
                  <UIcon name="i-lucide-map-pin" class="size-4 text-gray-500" />
                  {{ t('filtres.ville.titre') }}
                </p>
                <button v-if="selectedVille" class="text-xs text-main font-medium hover:underline" @click="clearVille">{{ t('filtres.reinitialiser') }}</button>
              </div>
              <div class="relative mb-2">
                <UIcon name="i-lucide-search" class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 size-3.5" />
                <input
                  v-model="villeSearchQuery"
                  type="text"
                  :placeholder="t('filtres.ville.rechercher')"
                  class="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 bg-gray-50 text-gray-900 placeholder-gray-400 focus:outline-none focus:border-gray-300 focus:bg-white text-xs"
                />
              </div>
              <div class="flex flex-wrap gap-1.5">
                <button
                  v-for="city in filteredCities"
                  :key="city.slug"
                  :class="[
                    'inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap',
                    selectedVille === city.slug
                      ? 'bg-main text-white shadow-sm'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200',
                  ]"
                  @click="selectVille(city.slug)"
                >
                  <span>{{ city.name }}</span>
                  <span :class="selectedVille === city.slug ? 'text-white/70' : 'text-gray-400'">{{ city.count }}</span>
                </button>
              </div>
              <p v-if="filteredCities.length === 0" class="text-xs text-gray-400 italic mt-1">
                {{ t('filtres.ville.aucune') }}
              </p>
            </div>

            <!-- ─── Filtre Catégorie ──────────────────────────────────── -->
            <div v-if="categories.length > 0">
              <div class="flex items-center justify-between mb-3">
                <p class="text-sm font-semibold text-gray-900">{{ t('filtres.categorie.titre') }}</p>
                <button v-if="selectedCategory !== 'all'" class="text-xs text-main font-medium hover:underline" @click="setCategory('all')">{{ t('filtres.reinitialiser') }}</button>
              </div>
              <div class="flex flex-wrap gap-2">
                <button
                  v-for="cat in categories"
                  :key="cat.value"
                  :class="[
                    'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap',
                    selectedCategory === cat.value
                      ? 'bg-main text-white shadow-sm'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200',
                  ]"
                  @click="setCategory(cat.value)"
                >
                  <UIcon :name="cat.icon" class="size-3.5" />
                  {{ cat.label }}
                </button>
              </div>
            </div>
          </div>
        </Transition>
      </div>
    </div>

    <!-- Popover backdrop (close on click outside) -->
    <div v-if="showFilters" class="fixed inset-0 z-40" @click="showFilters = false" />
  </div>
</template>

<i18n lang="json" src="../../../i18n/pages/components/recherche-page/RechercheSearchFilters.json"></i18n>
